import React, { useEffect, useState } from 'react';
import { Button } from './Button';

/**
 * The confirm dialog every action goes through. States its blast radius,
 * optionally collects form fields (children) and a note, runs the async
 * action with a busy state, and surfaces errors inline instead of alerts.
 */
interface ConfirmDialogProps {
  open: boolean;
  title: string;
  body?: React.ReactNode;
  children?: React.ReactNode;
  confirmLabel?: string;
  tone?: 'primary' | 'danger';
  onConfirm: () => Promise<void> | void;
  onClose: () => void;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open, title, body, children, confirmLabel = 'Confirm', tone = 'primary', onConfirm, onClose,
}) => {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) { setBusy(false); setErr(null); }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onClose]);

  if (!open) return null;

  const run = async () => {
    setBusy(true);
    setErr(null);
    try {
      await onConfirm();
      onClose();
    } catch (e) {
      setErr((e as Error).message || 'something went wrong');
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-stone-900/40" onClick={() => !busy && onClose()} />
      <div role="dialog" aria-modal="true" className="relative w-full max-w-md max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain bg-white rounded-lg border border-stone-200 p-5 shadow-xl">
        <h2 className="text-[15px] font-bold text-stone-900 mb-1.5">{title}</h2>
        {body && <div className="text-[13px] text-stone-600 leading-relaxed mb-3">{body}</div>}
        {children && <div className="mb-3 space-y-2.5">{children}</div>}
        {err && (
          <div className="mb-3 p-2.5 rounded-md bg-rose-50 border border-rose-200 text-[12px] text-rose-700">
            {err}
          </div>
        )}
        <div className="flex justify-end gap-2 [&>button]:flex-1 sm:[&>button]:flex-none">
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant={tone === 'danger' ? 'danger' : 'primary'} onClick={run} busy={busy}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
};

/** Small labelled input for dialog forms. */
export const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <label className="block">
    <span className="block text-[11px] font-semibold text-stone-500 mb-1">{label}</span>
    {children}
  </label>
);

export const inputCls =
  'w-full h-9 px-2.5 rounded-md border border-stone-300 bg-white focus:border-indigo-400 focus:outline-none text-[13px] text-stone-900';
